import { type FC, Fragment, useCallback, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { HeadingLevel } from "@viagogo/core/ui";
import { AddFeature } from "@viagogo/feature";
import type { EventGridItem, HomePageViewModel } from "src/http/types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { CookiePreferencesContext } from "src/shared/context/cookie-preferences-context";
import { LocationContext } from "src/shared/context/location-context";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { AppInstallBanner, AppInstallBannerPlacements } from "src/website-apps/shared/components/app-install-banner";
import {
  AppInstallInterstitialDesktop,
  AppInstallInterstitialMobile,
  AppInstallInterstitialPlacements,
  useAppInstallInterstitialVariant,
} from "src/website-apps/shared/components/app-install-interstitial";
import { CarouselProvider } from "src/website-apps/shared/components/responsive-carousel/CarouselIndexContext";
import { ContentLayout } from "src/website-apps/shared/context/ContentLayoutContext";
import { ContainerWithMargin, PaddedDiv } from "../styled-components";
import { EmailOptInPopup, isEligibleForPopupVariant, useEmailOptInPopupVariant } from "./email-opt-in-popup";
import { SurveySubmissionToast } from "./survey-submission-toast";
import { useCarouselComponents } from "./use-carousel-components";
import { UserEventToastOnHome } from "./UserEventToastOnHome";

interface Props {
  model: HomePageViewModel;
  selectedCarouselGenre: number;
  setSelectedCarouselGenre: (value: number) => void;
}

/**
 * Home Page Layout Body
 */
export const HomeLayoutBody: FC<Props> = ({ model, selectedCarouselGenre, setSelectedCarouselGenre }) => {
  const appContext = useAppContext();
  const createQueryFn = useCreateQueryFn();
  const isStubhub = useIsStubhub();
  const { showSurveySubmissionToast = false } = model;

  // Exp 103068: a null model field is the server's authoritative "fetched lazily by the
  // client" signal — the server only skips this fetch for variant users, and control always
  // serializes a (possibly empty) array. Gating on null rather than a client-side experiment
  // check keeps control fetch-free and cannot skew from the server's variant decision.
  const { data: fetchedUserEventOption } = useQuery<readonly EventGridItem[]>({
    queryKey: createQueryKey(QueryKeyPrefix.USER_EVENT_OPTION_HOME),
    queryFn: createQueryFn({ method: "GetUserEventOption", params: [] }),
    staleTime: Infinity,
    enabled: !model.userEventOption,
  });
  const userEventOption = model.userEventOption ?? fetchedUserEventOption;

  // Resolve the email opt-in popup experiment variant ONCE per render tree.
  // Participation records at one arm-agnostic moment: the email opt-in slot scrolling into
  // view. Every arm shares that trigger, so the split's denominators stay comparable; popup-arm
  // users whose gate is met (V1 2nd page view / V2 return visit) swap the banner for the popup
  // at that same moment. The recorder itself re-checks canParticipate at call time.
  const { variant: emailOptInPopupVariant, recordParticipation: recordEmailOptInParticipation } =
    useEmailOptInPopupVariant();
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const { hasSelectedPreferences } = useContext(CookiePreferencesContext);
  const [isPopupGateMet] = useState(() => isEligibleForPopupVariant(emailOptInPopupVariant));
  // The experiment targets anonymous desktop users who have accepted cookie consent — the same
  // population for every arm. onInView is withheld until eligible so the in-view tracker mounts
  // only once the user can actually participate.
  const isEmailOptInExperimentEligible = isDesktop && !appContext.isUserLoggedIn && hasSelectedPreferences;
  const swapEmailOptInBannerForPopup = isEmailOptInExperimentEligible && isPopupGateMet;
  const [isPopupTriggered, setIsPopupTriggered] = useState(false);
  const handleEmailOptInSlotInView = useCallback(() => {
    recordEmailOptInParticipation();
    if (swapEmailOptInBannerForPopup) setIsPopupTriggered(true);
  }, [swapEmailOptInBannerForPopup, recordEmailOptInParticipation]);

  // Read here (not inside useCarouselComponents) so the hook stays context-free/testable; the
  // NGS home banner re-checks football proximity when this changes (FS-1065).
  const [userLocation] = useContext(LocationContext);

  const { sortedComponents } = useCarouselComponents({
    model,
    selectedCarouselGenre,
    setSelectedCarouselGenre,
    swapEmailOptInBannerForPopup,
    onEmailOptInSlotInView: isEmailOptInExperimentEligible ? handleEmailOptInSlotInView : undefined,
    userLocation,
  });

  // Exp 103366: suppress interstitial for users with a recent native app session.
  const isEligibleForInterstitial = appContext.isUserLoggedIn && !appContext.isNativeApp && isStubhub;
  const { showInterstitial, loading: interstitialLoading } =
    useAppInstallInterstitialVariant(isEligibleForInterstitial);

  // Suppress the email opt-in popup while the UserEventToast is actually rendered in the same
  // bottom-right corner. UserEventToast reports its renderable state via onToastAvailabilityChange,
  // so the popup guard reflects whether the toast is truly on screen — not just whether the server
  // returned event data (entries may all be already-dismissed, in which case the toast renders
  // nothing and the corner is free for the popup).
  const [hasUserEventToast, setHasUserEventToast] = useState(false);

  return (
    <HeadingLevel>
      <PaddedDiv style={{ overflowX: "hidden" }}>
        <ContainerWithMargin fluid={[true, true, true, true, true]}>
          <ContentLayout>
            <UserEventToastOnHome userEventOption={userEventOption} onToastAvailabilityChange={setHasUserEventToast} />
            {sortedComponents
              .filter((carousel) => carousel.shouldRender)
              .map(({ carouselId, kind, render }, visibleIndex) => (
                <Fragment key={carouselId}>
                  <CarouselProvider index={visibleIndex} kind={kind}>
                    {render()}
                  </CarouselProvider>
                </Fragment>
              ))}
            {!appContext.isNativeApp && (
              <AppInstallBanner placement={AppInstallBannerPlacements.HOME} userId={appContext.userId} />
            )}
            {!interstitialLoading && showInterstitial && (
              <>
                <AddFeature
                  id={FeatureSetEnum.MHomeAppInstallInterstitial}
                  canParticipate={() => isEligibleForInterstitial}
                  feature={() => <AppInstallInterstitialMobile placement={AppInstallInterstitialPlacements.HOME} />}
                />
                <AddFeature
                  id={FeatureSetEnum.THomeAppInstallInterstitial}
                  canParticipate={() => isEligibleForInterstitial}
                  feature={() => <AppInstallInterstitialDesktop placement={AppInstallInterstitialPlacements.HOME} />}
                />
              </>
            )}
            {/* EmailSignupOnHome (legacy) has been removed. The email opt-in banner is
                  rendered inside the carousel list via useCarouselComponents, and the popup
                  is rendered below. Neither should fall back to the legacy component. */}
            {showSurveySubmissionToast ? <SurveySubmissionToast /> : null}
            <EmailOptInPopup
              privacyPolicyMessage={model.privacyPolicyMessage}
              isTriggered={isPopupTriggered}
              hasUserEventToast={hasUserEventToast}
            />
          </ContentLayout>
        </ContainerWithMargin>
      </PaddedDiv>
    </HeadingLevel>
  );
};
